import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  ErrorMessageComponent,
  LocationComponent
} from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import { DateRange } from '@com-delta-omnichannel/mach-core-js';
import { takeUntil } from 'rxjs/operators';
import { ViewSearchOptionsComponent } from '../shared/components/view-search-options/view-search-options.component';
import { StaysWidgetBaseComponent } from './stays-widget-base/stays-widget-base.component';
@Component({
  selector: 'idp-stays-widget',
  standalone: true,
  imports: [
    CommonModule,
    ViewSearchOptionsComponent,
    LocationComponent,
    ReactiveFormsModule,
    ErrorMessageComponent
  ],
  templateUrl: './stays-widget.component.html',
  styleUrls: ['./stays-widget.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class StaysWidgetComponent
  extends StaysWidgetBaseComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  ngAfterViewInit() {
    if (this.initializationService.getRequiredElements(this.content)) {
      this.initializeComponents();
      this.setupEventSubscriptions();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['locationSelected']?.currentValue) {
      this.content.locationContent.label =
        changes['locationSelected'].currentValue.name;
      this.updateDestination(this.content.locationContent.label);
    }
    if (changes['resetErrors']?.currentValue) {
      this.clearAllErrors();
    }
  }

  private updateDestination(destination: string): void {
    this.destinationErrorMessage = this.lifecycleService.updateDestination(
      this.staysWidgetForm,
      this.content,
      destination
    );
    if (this.isSubmitted) {
      this.eventService.emitErrorCountChange();
    }

    setTimeout(() => this.locationComponent?.focusField());
  }

  initializeComponents(): void {
    Object.assign(
      this,
      this.lifecycleService.initializeWidget(
        this.content,
        this.staysWidgetForm,
        () => this.isSubmitted,
        () => {
          this.isDatePickerOpen = false;
        }
      )
    );
  }

  displayOptions(): void {
    this.uiStateService.toggleExpandView();
    this.expandView = this.uiStateService.getCurrentExpandView();
    this.expandEmit.emit(this.expandView);
  }

  locationClicked(event: boolean): void {
    this.openModalEmit.emit(event);
  }

  formSubmit(): void {
    this.isSubmitted = true;
    const errors = this.lifecycleService.processFormSubmission(
      this.staysWidgetForm,
      this.content
    );
    if (this.staysWidgetForm.invalid) {
      this.getError(errors);
    }
    if (this.staysWidgetForm.valid) {
      this.formSubmitted.emit(this.content.mdpcid);
    }
  }

  setupEventSubscriptions(): void {
    this.eventService.onDateChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe((dates: DateRange) => this.updateFormDates(dates));
    this.eventService.onDateError$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => (this.datesErrorMessage = ''));
    this.eventService.onPassengerError$
      .pipe(takeUntil(this.destroy$))
      .subscribe((error: string) => (this.tooFewPeopleErrorMessage = error));
    this.eventService.onRoomError$
      .pipe(takeUntil(this.destroy$))
      .subscribe((error: string) => (this.tooFewRoomsErrorMessage = error));
    this.eventService.onMaxDatesError$
      .pipe(takeUntil(this.destroy$))
      .subscribe((error: string) => (this.maxDatesErrorMessage = error));
    this.eventService.onErrorCountChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.handleErrorCountChange());
  }

  onFocusFirstInvalidField(): void {
    this.focusFirstInvalidFieldHandler(this.staysWidgetForm);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
